import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import classNames from 'classnames';
import OkCancelModal from '@/shared/components/OkCancelModal';
import YesNoModal from '@/shared/components/YesNoModal';
import CheckboxList from '@/shared/components/input/CheckboxList';
import LoadingContainer from '@/shared/components/loading/LoadingContainer';
import ToastrService from '@/shared/services/ToastrService';
import { useGetSsoSettings } from '@/shared/hooks/settings/useGetSsoSettings';
import { useUpdateSsoSettings } from '@/shared/hooks/settings/useUpdateSsoSettings';
const NO_SSO = 0;
const ENTRA_SSO = 1;
const messages = defineMessages({
    SsoModalTitle: {
        id: 'SsoModalTitle',
        defaultMessage: 'Single sign-on (SSO)',
    },
    SsoAppliesToWholeTenant: {
        id: 'SsoAppliesToWholeTenant',
        defaultMessage: 'Single sign-on applies to your whole organization, not just the selected company.',
    },
    SsoOptionNone: {
        id: 'SsoOptionNone',
        defaultMessage: 'No single sign-on',
    },
    SsoOptionNoneDescription: {
        id: 'SsoOptionNoneDescription',
        defaultMessage: 'Users sign in with their e-mail address and password, plus two-factor authentication when enabled.',
    },
    SsoOptionEntra: {
        id: 'SsoOptionEntra',
        defaultMessage: 'Microsoft Entra ID',
    },
    SsoOptionEntraDescription: {
        id: 'SsoOptionEntraDescription',
        defaultMessage: 'Users sign in with the Microsoft work account of your organization.',
    },
    SsoEntraGuidelinesIntro: {
        id: 'SsoEntraGuidelinesIntro',
        defaultMessage: 'When Microsoft Entra ID single sign-on is enabled:',
    },
    SsoEntraGuidelineEmailMustMatch: {
        id: 'SsoEntraGuidelineEmailMustMatch',
        defaultMessage: "Users sign in via Microsoft Entra ID. The e-mail address of the AdminPulse user must match the user's Microsoft account.",
    },
    SsoEntraGuidelinePasswordReset: {
        id: 'SsoEntraGuidelinePasswordReset',
        defaultMessage: 'Password sign-in and password resets are disabled for these users.',
    },
    SsoEntraGuidelineTwoFa: {
        id: 'SsoEntraGuidelineTwoFa',
        defaultMessage: 'Two-factor authentication is managed in Microsoft Entra ID.',
    },
    SsoEntraGuidelineRelogin: {
        id: 'SsoEntraGuidelineRelogin',
        defaultMessage: 'Users that are signed in may be asked to sign in again.',
    },
    SsoCurrentProviderManagedBySupport: {
        id: 'SsoCurrentProviderManagedBySupport',
        defaultMessage: 'Your organization currently uses {provider} single sign-on, which is managed by AdminPulse support. You can still disable it or switch to Microsoft Entra ID here.',
    },
    SsoExcludedUsersTitle: {
        id: 'SsoExcludedUsersTitle',
        defaultMessage: 'Users excluded from single sign-on',
    },
    SsoExcludedUsersDescription: {
        id: 'SsoExcludedUsersDescription',
        defaultMessage: 'Excluded users can keep signing in with their e-mail address and password, for example users without a Microsoft account in your organization. They can still use single sign-on voluntarily.',
    },
    SsoSearchUsers: {
        id: 'SsoSearchUsers',
        defaultMessage: 'Search users',
    },
    SsoConfirmEnableTitle: {
        id: 'SsoConfirmEnableTitle',
        defaultMessage: 'Enable single sign-on?',
    },
    SsoConfirmEnableMessage: {
        id: 'SsoConfirmEnableMessage',
        defaultMessage: 'All users except excluded users will have to sign in with their Microsoft account from now on. Users that are signed in may be asked to sign in again.',
    },
    SsoConfirmDisableTitle: {
        id: 'SsoConfirmDisableTitle',
        defaultMessage: 'Disable single sign-on?',
    },
    SsoConfirmDisableMessage: {
        id: 'SsoConfirmDisableMessage',
        defaultMessage: "All users will sign in with their e-mail address and password from now on. Users that don't have a password yet can request one via 'Forgot password' on the sign-in page.",
    },
    SsoSettingsSaved: {
        id: 'SsoSettingsSaved',
        defaultMessage: 'The single sign-on settings have been saved.',
    },
    Save: {
        id: 'Save',
        defaultMessage: 'Save',
    },
});
const providerCardStyle = (selected) => ({
    cursor: 'pointer',
    height: '100%',
    marginBottom: 10,
    borderWidth: 2,
    borderColor: selected ? '#31708f' : undefined,
});
const ProviderCard = ({ selected, title, description, onClick }) => (_jsx("div", Object.assign({ className: "panel panel-default", style: providerCardStyle(selected), onClick: onClick }, { children: _jsxs("div", Object.assign({ className: "panel-body" }, { children: [_jsxs("div", Object.assign({ className: "bold" }, { children: [_jsx("i", { className: classNames('fa fa-fw margin5R', {
                            'fa-dot-circle-o': selected,
                            'fa-circle-o': !selected,
                        }) }), title] })), _jsx("div", Object.assign({ className: "text-muted margin5T" }, { children: description }))] })) })));
const SsoSettingsForm = ({ show, onClose, settings }) => {
    var _a;
    const intl = useIntl();
    const { updateSsoSettings, isLoading: saving } = useUpdateSsoSettings();
    const currentProvider = (_a = settings.ssoProvider) !== null && _a !== void 0 ? _a : NO_SSO;
    const isSupportManagedProvider = currentProvider > ENTRA_SSO;
    const [selectedProvider, setSelectedProvider] = useState(isSupportManagedProvider ? null : currentProvider);
    const [excludedUserIds, setExcludedUserIds] = useState(() => (settings.users || []).filter(u => u.excludedFromSso).map(u => u.userId));
    const [search, setSearch] = useState('');
    const [showConfirm, setShowConfirm] = useState(false);
    const initialExcludedUserIds = useMemo(() => (settings.users || []).filter(u => u.excludedFromSso).map(u => u.userId), [settings.users]);
    const exclusionsChanged = excludedUserIds.length !== initialExcludedUserIds.length ||
        excludedUserIds.some(id => !initialExcludedUserIds.includes(id));
    const providerChanged = selectedProvider !== null && selectedProvider !== currentProvider;
    const dirty = selectedProvider !== null && (providerChanged || exclusionsChanged);
    const userItems = (settings.users || [])
        .filter(u => `${u.name} ${u.email}`.toLowerCase().includes(search.toLowerCase()))
        .map(u => ({
        id: u.userId,
        label: `${u.name} (${u.email})`,
        selected: excludedUserIds.includes(u.userId),
    }));
    const handleUserToggled = (item, selected) => {
        setExcludedUserIds(prev => (selected ? [...prev, item.id] : prev.filter(id => id !== item.id)));
    };
    const save = async () => {
        if (selectedProvider === null)
            return;
        try {
            await updateSsoSettings({ ssoProvider: selectedProvider, excludedUserIds });
            ToastrService.success(intl.formatMessage(messages.SsoSettingsSaved));
            onClose();
        }
        catch (_a) {
            // error toastr already shown by useUpdateSsoSettings hook
        }
    };
    const handleSave = () => {
        if (!dirty)
            return;
        if (providerChanged) {
            setShowConfirm(true);
            return;
        }
        save();
    };
    const handleConfirm = async () => {
        setShowConfirm(false);
        await save();
    };
    const enabling = providerChanged && selectedProvider === ENTRA_SSO;
    return (_jsxs(_Fragment, { children: [_jsxs(OkCancelModal, Object.assign({ title: intl.formatMessage(messages.SsoModalTitle), show: show, onClose: onClose, onSave: handleSave, disabled: saving || !dirty, okLabel: intl.formatMessage(messages.Save), size: "lg", disableFullScreen: true }, { children: [_jsx("p", Object.assign({ className: "text-muted" }, { children: intl.formatMessage(messages.SsoAppliesToWholeTenant) })), isSupportManagedProvider && (_jsx("div", Object.assign({ className: "alert alert-warning" }, { children: intl.formatMessage(messages.SsoCurrentProviderManagedBySupport, {
                            provider: ['Google', 'PwC', 'Visma Connect'][currentProvider - 2] || 'SSO',
                        }) }))), _jsxs("div", Object.assign({ className: "row" }, { children: [_jsx("div", Object.assign({ className: "col-sm-6" }, { children: _jsx(ProviderCard, { selected: selectedProvider === NO_SSO, title: intl.formatMessage(messages.SsoOptionNone), description: intl.formatMessage(messages.SsoOptionNoneDescription), onClick: () => setSelectedProvider(NO_SSO) }) })), _jsx("div", Object.assign({ className: "col-sm-6" }, { children: _jsx(ProviderCard, { selected: selectedProvider === ENTRA_SSO, title: intl.formatMessage(messages.SsoOptionEntra), description: intl.formatMessage(messages.SsoOptionEntraDescription), onClick: () => setSelectedProvider(ENTRA_SSO) }) }))] })), selectedProvider === ENTRA_SSO && (_jsxs(_Fragment, { children: [_jsxs("div", Object.assign({ className: "alert alert-info" }, { children: [_jsx("div", { children: intl.formatMessage(messages.SsoEntraGuidelinesIntro) }), _jsxs("ul", Object.assign({ style: { paddingLeft: 18 } }, { children: [_jsx("li", { children: intl.formatMessage(messages.SsoEntraGuidelineEmailMustMatch) }), _jsx("li", { children: intl.formatMessage(messages.SsoEntraGuidelinePasswordReset) }), _jsx("li", { children: intl.formatMessage(messages.SsoEntraGuidelineTwoFa) }), _jsx("li", { children: intl.formatMessage(messages.SsoEntraGuidelineRelogin) })] }))] })), _jsx("div", Object.assign({ className: "bold" }, { children: intl.formatMessage(messages.SsoExcludedUsersTitle) })), _jsx("p", Object.assign({ className: "text-muted" }, { children: intl.formatMessage(messages.SsoExcludedUsersDescription) })), _jsx("input", { type: "text", className: "form-control margin10B", placeholder: intl.formatMessage(messages.SsoSearchUsers), value: search, onChange: e => setSearch(e.target.value) }), _jsx("div", Object.assign({ style: { maxHeight: 240, overflowY: 'auto' } }, { children: _jsx(CheckboxList, { items: userItems, onItemChanged: handleUserToggled }) }))] }))] })), showConfirm && (_jsx(YesNoModal, { show: showConfirm, title: intl.formatMessage(enabling ? messages.SsoConfirmEnableTitle : messages.SsoConfirmDisableTitle), message: intl.formatMessage(enabling ? messages.SsoConfirmEnableMessage : messages.SsoConfirmDisableMessage), onSave: handleConfirm, onNo: () => setShowConfirm(false), onClose: () => setShowConfirm(false) }))] }));
};
// The settings (including the tenant's user list) are only fetched when the modal is opened.
const SsoSettingsModal = ({ show, onClose }) => {
    const intl = useIntl();
    const { data: settings, isLoading } = useGetSsoSettings(show);
    if (isLoading || !settings) {
        return (_jsx(OkCancelModal, Object.assign({ title: intl.formatMessage(messages.SsoModalTitle), show: show, onClose: onClose, onSave: () => undefined, disabled: true, okLabel: intl.formatMessage(messages.Save), size: "lg", disableFullScreen: true }, { children: _jsx(LoadingContainer, { isLoading: true }) })));
    }
    return _jsx(SsoSettingsForm, { show: show, onClose: onClose, settings: settings });
};
export default SsoSettingsModal;
